import { fontFamily, fontSize, lineHeight, fontWeight, radius, spacing, motion } from './primitives';
import type { SemanticPalette } from './semantic';

/**
 * Semantic palette → CSS variable map.
 * UI dışı (TipTap, custom components) bu değişkenleri okur:
 *   color: var(--ds-text-primary);
 *   background: var(--ds-sidebar-active-bg);
 */
export function semanticToCssVars(s: SemanticPalette): Record<string, string> {
  return {
    '--ds-brand': s.brand,
    '--ds-brand-hover': s.brandHover,
    '--ds-brand-active': s.brandActive,
    '--ds-brand-subtle': s.brandSubtle,
    '--ds-brand-contrast': s.brandContrast,

    '--ds-bg-canvas': s.bgCanvas,
    '--ds-bg-elevated': s.bgElevated,
    '--ds-bg-subtle': s.bgSubtle,
    '--ds-bg-muted': s.bgMuted,
    '--ds-bg-inverse': s.bgInverse,

    '--ds-sidebar-bg': s.sidebarBg,
    '--ds-sidebar-item-hover-bg': s.sidebarItemHoverBg,
    '--ds-sidebar-item-active-bg': s.sidebarItemActiveBg,
    '--ds-sidebar-item-active-accent': s.sidebarItemActiveAccent,
    '--ds-topbar-bg': s.topbarBg,
    '--ds-card-bg': s.cardBg,

    '--ds-text-primary': s.textPrimary,
    '--ds-text-secondary': s.textSecondary,
    '--ds-text-tertiary': s.textTertiary,
    '--ds-text-disabled': s.textDisabled,
    '--ds-text-on-brand': s.textOnBrand,
    '--ds-text-on-inverse': s.textOnInverse,

    '--ds-border-subtle': s.borderSubtle,
    '--ds-border-default': s.borderDefault,
    '--ds-border-strong': s.borderStrong,
    '--ds-border-focus': s.borderFocus,

    '--ds-success': s.success,
    '--ds-success-subtle': s.successSubtle,
    '--ds-warning': s.warning,
    '--ds-warning-subtle': s.warningSubtle,
    '--ds-danger': s.danger,
    '--ds-danger-subtle': s.dangerSubtle,
    '--ds-info': s.info,
    '--ds-info-subtle': s.infoSubtle,

    '--ds-status-draft': s.statusDraft,
    '--ds-status-review': s.statusReview,
    '--ds-status-published': s.statusPublished,
    '--ds-status-archived': s.statusArchived,

    '--ds-shadow-1': s.shadow1,
    '--ds-shadow-2': s.shadow2,
    '--ds-shadow-3': s.shadow3,
    '--ds-shadow-4': s.shadow4,
    '--ds-shadow-5': s.shadow5,
  };
}

export const baseCssVars: Record<string, string> = {
  '--ds-font-sans': fontFamily.sans,
  '--ds-font-mono': fontFamily.mono,

  '--ds-fs-xs': fontSize.xs,
  '--ds-fs-sm': fontSize.sm,
  '--ds-fs-base': fontSize.base,
  '--ds-fs-md': fontSize.md,
  '--ds-fs-lg': fontSize.lg,
  '--ds-fs-xl': fontSize.xl,
  '--ds-fs-2xl': fontSize['2xl'],
  '--ds-fs-3xl': fontSize['3xl'],

  '--ds-lh-tight': String(lineHeight.tight),
  '--ds-lh-normal': String(lineHeight.normal),
  '--ds-lh-relaxed': String(lineHeight.relaxed),

  '--ds-fw-regular': String(fontWeight.regular),
  '--ds-fw-medium': String(fontWeight.medium),
  '--ds-fw-semibold': String(fontWeight.semibold),
  '--ds-fw-bold': String(fontWeight.bold),

  '--ds-radius-xs': `${radius.xs}px`,
  '--ds-radius-sm': `${radius.sm}px`,
  '--ds-radius-md': `${radius.md}px`,
  '--ds-radius-lg': `${radius.lg}px`,
  '--ds-radius-xl': `${radius.xl}px`,
  '--ds-radius-full': `${radius.full}px`,

  '--ds-space-1': spacing[1],
  '--ds-space-2': spacing[2],
  '--ds-space-3': spacing[3],
  '--ds-space-4': spacing[4],
  '--ds-space-5': spacing[5],
  '--ds-space-6': spacing[6],
  '--ds-space-8': spacing[8],

  '--ds-motion-fast': motion.fast,
  '--ds-motion-base': motion.base,
  '--ds-motion-slow': motion.slow,

  '--ds-sidebar-width': '248px',
  '--ds-sidebar-width-collapsed': '64px',
  '--ds-topbar-height': '60px',
};

export function buildVarString(map: Record<string, string>): string {
  return Object.entries(map)
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}
